import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminUserForm from './AdminUserForm';
import AdminUserProgressModal from './AdminUserProgressModal'; // Import the new modal

const AdminUserManager = () => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [isRoleFormOpen, setIsRoleFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    // --- (NEW) State for the progress modal ---
    const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
    const [viewingUserProgress, setViewingUserProgress] = useState(null);

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('app_token');
            const params = { search: searchTerm };
            const response = await axios.get('/api/admin/users', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            setUsers(response.data);
            setError(null);
        } catch (err) {
            setError("Failed to load users.");
        } finally {
            setIsLoading(false);
        }
    }, [searchTerm]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchUsers();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [fetchUsers]);

    const handleOpenRoleForm = (user) => {
        setEditingUser(user);
        setIsRoleFormOpen(true);
    };

    const handleCloseRoleForm = () => {
        setEditingUser(null);
        setIsRoleFormOpen(false);
    };

    // --- (NEW) Functions to handle the progress modal ---
    const handleOpenProgressModal = (user) => {
        setViewingUserProgress(user);
        setIsProgressModalOpen(true);
    };

    const handleCloseProgressModal = () => {
        setViewingUserProgress(null);
        setIsProgressModalOpen(false);
    };

    const handleSaveRole = async (userId, role) => {
        const token = localStorage.getItem('app_token');
        await axios.put(`/api/admin/users/${userId}/role`, 
            { role },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchUsers();
        handleCloseRoleForm();
    };

    const handleDelete = async (userId) => {
        if (window.confirm('Are you sure you want to PERMANENTLY delete this user and all their content? This cannot be undone.')) {
            try {
                const token = localStorage.getItem('app_token');
                await axios.delete(`/api/admin/users/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchUsers();
            } catch (err) {
                alert('Error deleting user.');
            }
        }
    };

    if (isLoading && !users.length) return <p>Loading users...</p>;
    if (error) return <p className="error-message">{error}</p>;

    return (
        <div className="admin-content-manager">
            {isRoleFormOpen && (
                <AdminUserForm 
                    user={editingUser} 
                    onSaveRole={handleSaveRole} 
                    onCancel={handleCloseRoleForm} 
                />
            )}
            {/* --- (NEW) Render the progress modal conditionally --- */}
            {isProgressModalOpen && (
                <AdminUserProgressModal
                    user={viewingUserProgress}
                    onCancel={handleCloseProgressModal}
                />
            )}

            <div className="admin-content-header">
                <h3 className="admin-section-title">Manage Users</h3>
                <input 
                    type="text"
                    placeholder="Search by name or email..."
                    className="admin-search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>User</th>
                            <th>Role</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.id}>
                                <td>{user.id}</td>
                                <td>
                                    <div className="user-cell">
                                        <img 
                                          src={user.profile_picture || 'https://placehold.co/40x40/16a085/ffffff?text=U'}
                                          alt={user.name} 
                                          className="user-avatar"
                                          onError={e => { e.target.onerror = null; e.target.src = 'https://placehold.co/40x40/16a085/ffffff?text=U'; }}
                                        />
                                        <div>
                                            <div className="user-name">{user.name}</div>
                                            <div className="user-email">{user.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td><span className={`role-badge role-${user.role}`}>{user.role}</span></td>
                                <td className="admin-table-actions">
                                    {/* --- (NEW) Button to open progress modal --- */}
                                    <button onClick={() => handleOpenProgressModal(user)} className="action-btn edit" title="View Progress">
                                        <span className="material-icons">show_chart</span>
                                    </button>
                                    <button onClick={() => handleOpenRoleForm(user)} className="action-btn edit" title="Edit Role">
                                        <span className="material-icons">manage_accounts</span>
                                    </button>
                                    <button onClick={() => handleDelete(user.id)} className="action-btn delete" title="Delete User">
                                        <span className="material-icons">delete_forever</span>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminUserManager;
